import { zodResolver } from "@hookform/resolvers/zod"
import { ArrowLeft } from "react-feather"
import { useForm } from "react-hook-form"
import { Link, useNavigate } from "react-router-dom"
import { toast } from "sonner"
import { z } from "zod"
import { useChangePassword } from "@/api/client/auth"
import { changePasswordRequestSchema } from "@/api/client/auth/schemas"
import { Button } from "@/components/ui/button/button"
import { Card, CardContent } from "@/components/ui/card/card"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form/form"
import { Input } from "@/components/ui/input/input"
import { cn } from "@/lib/utils"

const changePasswordFormSchema = changePasswordRequestSchema
  .extend({
    confirmPassword: z.string().min(1, "パスワードを確認してください"),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "パスワードが一致しません",
    path: ["confirmPassword"],
  })

type ChangePasswordFormValues = z.infer<typeof changePasswordFormSchema>

type TabItem = {
  label: string
  path: string
  isActive: boolean
}

function ProfileEditTabs({ tabs }: { tabs: TabItem[] }) {
  return (
    <div className="bg-slate-100 rounded-md p-1 flex">
      {tabs.map((tab) => (
        <Link
          key={tab.path}
          to={tab.path}
          className={cn(
            "flex-1 text-center py-1.5 px-3 text-sm font-semibold rounded-sm transition-all",
            tab.isActive ? "bg-white text-primary shadow-sm" : "text-slate-500 hover:text-slate-700"
          )}
        >
          {tab.label}
        </Link>
      ))}
    </div>
  )
}

export function ChangePasswordPage() {
  const navigate = useNavigate()

  const form = useForm<ChangePasswordFormValues>({
    resolver: zodResolver(changePasswordFormSchema),
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
  })

  const { mutateAsync: changePassword, isPending } = useChangePassword({
    onSuccess: () => {
      toast.success("パスワードを変更しました")
      navigate("/profile")
    },
    onError: () => {
      toast.error("パスワードの変更に失敗しました")
    },
  })

  const tabs: TabItem[] = [
    { label: "基本情報", path: "/profile/edit", isActive: false },
    { label: "メールアドレス", path: "/profile/edit/change-email", isActive: false },
    { label: "パスワード", path: "/profile/edit/change-password", isActive: true },
  ]

  const handleSubmit = async (values: ChangePasswordFormValues) => {
    const { currentPassword, newPassword } = values
    await changePassword({ currentPassword, newPassword })
  }

  const handleCancel = () => {
    navigate("/profile")
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center gap-2.5">
        <Button variant="outline" size="sm" onClick={() => navigate("/profile")}>
          <ArrowLeft className="size-4" />
          戻る
        </Button>
        <h1 className="text-base font-semibold text-primary">プロフィール設定</h1>
      </div>

      {/* Tabs */}
      <ProfileEditTabs tabs={tabs} />

      {/* Form */}
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-5">
          <Card>
            <CardContent className="pt-5 space-y-4">
              {/* Title Section */}
              <div className="pb-1">
                <h2 className="text-xl font-bold text-slate-600">パスワード変更</h2>
                <p className="text-xs text-slate-500 mt-1">アカウントのパスワードを変更します</p>
              </div>

              {/* Current Password */}
              <FormField
                control={form.control}
                name="currentPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-semibold text-slate-600">現在のパスワード</FormLabel>
                    <FormControl>
                      <Input type="password" placeholder="********" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* New Password */}
              <FormField
                control={form.control}
                name="newPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-semibold text-slate-600">新しいパスワード</FormLabel>
                    <FormControl>
                      <Input type="password" placeholder="********" {...field} />
                    </FormControl>
                    <p className="text-xs text-slate-500">
                      パスワードは8文字以上で、半角英数字で入力してください
                    </p>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Confirm Password */}
              <FormField
                control={form.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-semibold text-slate-600">パスワードの確認</FormLabel>
                    <FormControl>
                      <Input type="password" placeholder="********" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex gap-2.5 justify-end">
            <Button type="button" variant="outline" onClick={handleCancel} disabled={isPending}>
              キャンセル
            </Button>
            <Button type="submit" loading={isPending}>
              更新する
            </Button>
          </div>
        </form>
      </Form>
    </div>
  )
}
